import React, { useCallback } from 'react'
import { NavLink } from 'react-router-dom'
import styled, { css } from 'styled-components'
import _ from 'underscore'

import GA4 from 'helpers/ga4'

import { useSessionContext } from 'hooks/SessionContext'

import Tags from 'pages/shared/Alert/Tags'

import { alertCodeDisplayValue } from 'helpers/alerts'

import { AlertDescription } from 'types/Alert'

import { color } from 'styles/colors'

type Props = {
  alertDescriptions: AlertDescription[];
  query: string | null;
}

const CATEGORY_NAMES = {
  'CFG': 'Configuration Errors',
  'ERR': 'Communicating Units',
  'EVT': 'Event Alerts',
  'SCH': 'Scheduling Errors',
  'SDA': 'Smart Diagnostics Alerts',
  'SOP': 'Sensor Faults',
  'TSO': 'Wireless Sensor Faults',
  'XLB': 'Embedded Bridge Error'
}

const AlertCodeList = ({ alertDescriptions, query = null }: Props) => {
  const { session: { isAlarmDescriptionAdmin: isAlertDescriptionAdmin } } = useSessionContext()

  const onAlertCodeClick = useCallback((code) => {
    const eventData = { category: 'Alert Lookup Page', action: 'Click Alert Code', label: code }

    GA4.sendEvent(eventData)
  }, [])

  const codeParts = _.map(alertDescriptions, (description) => {
    const fullCodeDisplayValue = alertCodeDisplayValue(description.code)

    return {
      fullCode: fullCodeDisplayValue,
      draft: description.draft,
      tags: description.tags,
      category: fullCodeDisplayValue.slice(0, 3),
      isSda: fullCodeDisplayValue.slice(0, 3) === 'SDA',
      categoryDisplayValue: fullCodeDisplayValue.slice(0, 3),
      section: fullCodeDisplayValue.slice(4, 7),
      subsection: fullCodeDisplayValue.slice(8, 10),
      prefix: fullCodeDisplayValue.slice(0, 8),
      description: description.description,
      severity: description.severity
    }
  })

  const categories =
    _.chain(codeParts)
      .uniq((codeParts) => codeParts.category)
      .map((parts) => (
        {
          value: parts.category,
          displayValue: parts.categoryDisplayValue
        }
      ))
      .sortBy((category) => category.displayValue)
      .value()

  const sectionsForCategory = (category) => (
    _.chain(codeParts)
      .filter((parts) => parts.category === category)
      .map((parts) => parts.section)
      .uniq()
      .sort()
      .value()
  )

  const codesForSection = (category, section) => (
    _.chain(codeParts)
      .filter((parts) => parts.category === category && parts.section === section)
      .sortBy((parts) => parts.subsection)
      .value()
  )

  return (
    <>
      <div data-testid="codes">
        {_.map(categories, (category) => (
          <S.Category data-testid={`category-${category.value}`} id={category.value} key={category.value}>
            <S.Header>
              <S.Title>{category.displayValue}</S.Title>
              <S.CategoryDescription>({CATEGORY_NAMES[category.value]})</S.CategoryDescription>
            </S.Header>

            {_.map(sectionsForCategory(category.value), (section) => (
              <S.Section data-testid={`section-${category.value}-${section}`} key={section}>
                <S.AlertCodes>
                  {_.map(codesForSection(category.value, section), (code) => {
                    if (!code.draft || (code.draft && isAlertDescriptionAdmin)) {
                      return (
                        <div key={code.fullCode}>
                          <S.AlertCodeLink
                            to={`?q=${query}&showModal=true&id=${code.fullCode}`}
                            onClick={() => onAlertCodeClick(alertCodeDisplayValue(code.fullCode))}
                            data-testid={`${code.fullCode}`}
                          >
                            <S.Code draft={code.draft}>{code.fullCode}</S.Code>
                            <S.Severity severity={code.severity} sda={code.isSda} />
                            <S.CodeDescription>{code.description}</S.CodeDescription>
                          </S.AlertCodeLink>
                          <Tags tags={code.tags} />
                        </div>
                      )
                    }
                  })}
                </S.AlertCodes>
              </S.Section>
            )
            )}

          </S.Category>
        ))}
      </div>
    </>
  )
}

export default AlertCodeList

const S = {
  CategoryShortcutBar: styled.div`
    margin-top: 30px;
  `,
  CategoryShortcut: styled.a`
    margin-right: 25px;
  `,
  Category: styled.div`
    margin-top: 30px;

    &:first-child > :first-child > a {
      display: none;
    }
  `,
  Header: styled.div`
    position: relative;
    padding-bottom: 5px;
    border-bottom: 1px solid ${color('divider')};
  `,
  Title: styled.h2`
    display: inline-block;
    margin-right: 10px;
    color: ${color('titleText')};
  `,
  CategoryDescription: styled.span`
    position: absolute;
    bottom: 10px;
  `,
  Section: styled.div`
    margin-top: 15px;
  `,
  SectionNumber: styled.span`
    font-weight: bold;
    color: ${color('alertSectionNumber')};
    display: none;
  `,
  AlertCodes: styled.span`
    display: inline-table;
    max-width: 90%;
  `,
  AlertCodeLink: styled(NavLink)`
    display: inline-block;
    line-height: 26px;
    padding: 0px 6px;
    margin-left: -6px;
  `,
  Code: styled.span.withConfig({
    shouldForwardProp: (prop) => !['draft'].includes(prop)
  })<{ draft: boolean | undefined }>`
    font-weight: normal;

    ${({ draft }) => draft
    ? css`
        margin: 5px -4px 0 -4px;
        padding: 1px 4px;
        border-radius: 3px;
        background-color: ${color('draftAlert')};
        color: ${color('draftAlertText')};
        `
    : css`
      margin: 5px 0 0 0;
      color: ${color('primaryText')};
    `};
  `,
  CodeDescription: styled.span`
    margin-left: 7px;
  `,
  Tag: styled.span`
    display: inline-block;
    background-color: ${color('tagBackground')};
    color: ${color('tagText')};
    border-radius: 5px;
    margin-left: 10px;
    padding 0 5px;
    font-weight: bold;
    cursor: default;
  `,
  Severity: styled.div.withConfig({
    shouldForwardProp: (prop) => !['severity', 'sda'].includes(prop)
  })<{ severity: string | null, sda: boolean }>`
    display: inline-block;
    width: 4px;
    height: 13px;
    margin-bottom: -2px;
    margin-left: 8px;

    ${({ severity, sda }) => {
    if (!sda) {
      return css`background-color: ${color(`${severity}Alert`)};`
    } else if (severity === 'major') {
      return css`background-color: ${color(`${severity}SdaAlert`)};`
    } else if (severity === 'minor') {
      return css`border: 1px solid ${color(`${severity}SdaAlert`)};`
    }
  }}
  `
}
